import type FlowModel from 'garaje/models/flow';
import type LocationModel from 'garaje/models/location';
import type LocationSubscriptionModel from 'garaje/models/location-subscription';
import type SubscriptionModel from 'garaje/models/subscription';
import type StateService from 'garaje/services/state';

/**
 * Feature flag gating the Mixed Plan Billing capability. MPB is a permanent
 * shipping capability, not a kill switch — keep this constant in sync with
 * the flag name in feature-flag config. The repo-wide sweep of existing
 * magic-string callers is tracked separately.
 */
export const MPB_FEATURE_FLAG = 'company-has-mixed-plan-billing';

/**
 * Narrow projection of StateService — keeps this util from being coupled to
 * the global state singleton's full surface area and clarifies what's
 * actually consumed.
 */
export type EligibilityStateLike = Pick<StateService, 'currentLocation' | 'locationVisitorsSubscription'>;

/**
 * Locations on the Visitors Enterprise tier. The Chargebee `enterprise-1` plan
 * variant is also counted (LocationSubscription.isEnterprisePlan strict-matches
 * `'enterprise'`, but `enterprise-1` is a real Enterprise tier — see the
 * `plans` array in `app/models/subscription.ts`).
 *
 * Resolution path:
 *   - Under MPB the canonical data source is `state.locationVisitorsSubscription`
 *     (populated by `subscription-state` from the `scoped-subscription`
 *     endpoint). Scoped-subscription responses carry no `relationships.location`
 *     linkage from the billing service, so the foreign-key approach used by
 *     ordinary location-subscription records doesn't work here.
 *   - When `location` matches `state.currentLocation` we read directly from
 *     state. For other locations under MPB no preloaded data exists, so we
 *     fall through to `location.visitorsSubscription` (the inverse-populated
 *     relationship). Under MPB that relationship is always empty because
 *     scoped-subscription declares `inverse: null`.
 *
 * The `failOpen` option controls behavior when neither path resolves any
 * sub data: pass `true` to treat unknown as "potentially enterprise"
 * (preferred for picker gating where backend remains the final gate),
 * or leave default `false` for strict tier checks.
 */
export function isLocationVisitorsEnterprise(
  location: LocationModel,
  state?: EligibilityStateLike,
  opts?: { failOpen?: boolean },
): boolean {
  const sub = findVisitorsSubscriptionForLocation(location, state);
  if (sub === undefined || sub === null) {
    return opts?.failOpen ?? false;
  }
  const plan = sub.plan;
  return plan === 'enterprise' || plan === 'enterprise-1';
}

function findVisitorsSubscriptionForLocation(
  location: LocationModel,
  state: EligibilityStateLike | undefined,
): LocationSubscriptionModel | SubscriptionModel | null | undefined {
  if (state && location.id && location.id === state.currentLocation?.id) {
    const sub = state.locationVisitorsSubscription;
    if (sub) return sub;
  }
  return location.visitorsSubscription;
}

/**
 * Lookup state for a location's scoped-subscription fetch. The `loaded`
 * shape carries the derived feature flags the pickers need so callers
 * don't have to re-traverse the subscription model.
 */
export type ScopedSubLookup =
  | { state: 'loading' }
  | { state: 'loaded'; plan: string | null; canAccessGlobalSignInFlows: boolean }
  | { state: 'error' };

/**
 * Secondary-locations picker eligibility (multi-location-invites is
 * Enterprise-only, so plan-tier is the right gate). Fails open on
 * missing/loading/error so a transient billing failure can't
 * cascade-disable every secondary.
 */
export function isLocationEligibleFromScopedMap(
  locationId: string,
  isMpbEnabled: boolean,
  scopedSubsByLocation: ReadonlyMap<string, ScopedSubLookup>,
): boolean {
  if (!isMpbEnabled) return true;
  // eslint-disable-next-line ember/use-ember-get-and-set
  const entry = scopedSubsByLocation.get(locationId);
  if (!entry || entry.state !== 'loaded') return true;
  if (entry.plan === null) return false;
  return entry.plan === 'enterprise' || entry.plan === 'enterprise-1';
}

/**
 * Flow-picker eligibility for global visitor types. Gates on the
 * `global_sign_in_flows` feature (present on Premium and Enterprise per
 * `billing/lib/assets/features_sync.csv`), NOT the plan tier — a plan-tier
 * check would incorrectly disable global flows for Premium customers.
 * Fails open on missing/loading/error.
 */
export function isLocationGlobalFlowsCapableFromMap(
  locationId: string,
  isMpbEnabled: boolean,
  scopedSubsByLocation: ReadonlyMap<string, ScopedSubLookup>,
): boolean {
  if (!isMpbEnabled) return true;
  // eslint-disable-next-line ember/use-ember-get-and-set
  const entry = scopedSubsByLocation.get(locationId);
  if (!entry || entry.state !== 'loaded') return true;
  return entry.canAccessGlobalSignInFlows;
}

/**
 * Flow-picker eligibility for the visitor-type dropdown (PR #19623). Global
 * visitor types require the primary location to carry the
 * `global_sign_in_flows` feature on its visitors subscription. Local flows
 * are always eligible. When a scoped-sub map is provided the helper prefers
 * it — that closes the in-form-primary-switch gap where
 * `state.locationVisitorsSubscription` is stale for the new primary.
 * Otherwise falls back to the relationship/state path with fail-open
 * semantics — backend remains the final gate.
 */
export function isFlowEligibleForLocation(
  flow: Pick<FlowModel, 'isGlobalChild'>,
  location: LocationModel | null | undefined,
  isMpbEnabled: boolean,
  state?: EligibilityStateLike,
  scopedSubsByLocation?: ReadonlyMap<string, ScopedSubLookup>,
  locationId?: string | null,
): boolean {
  if (!isMpbEnabled) return true;
  if (!flow.isGlobalChild) return true;
  // The scoped-sub map is keyed by location id and is the authoritative
  // in-form source, so consult it before requiring a materialized
  // LocationModel. `peekRecord('location', id)` can return null for an
  // edit/scheduled/cross-location primary even though its scoped-sub was
  // fetched — gating on the object would silently fail open in that case.
  const primaryId = location?.id ?? locationId ?? null;
  if (scopedSubsByLocation && primaryId) {
    return isLocationGlobalFlowsCapableFromMap(primaryId, isMpbEnabled, scopedSubsByLocation);
  }
  // Reached only when no id could be derived (no map passed, or both
  // location and locationId are null) — fail open, the backend stays the
  // final gate. Not dead code: the controller calls this without a map.
  if (!location) return true;
  const sub = findVisitorsSubscriptionForLocation(location, state);
  if (sub === undefined || sub === null) return true; // fail-open
  return sub.canAccessGlobalSignInFlows;
}
